"use client"

import * as React from "react"
import { Heart, MessageCircle, Share, MoreHorizontal, LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UIAvatar, UIAvatarFallback, UIAvatarImage } from '../display/ui-avatar'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardPostProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  author: {
    name: string
    username?: string
    avatarUrl?: string
  }
  content: string
  timestamp?: string | Date
  likes?: number
  comments?: number
  shares?: number
  liked?: boolean
  imageUrl?: string
  imageAlt?: string
  onLike?: () => void
  onComment?: () => void
  onShare?: () => void
  onMenuClick?: () => void
  actions?: Array<{
    icon: LucideIcon
    label: string
    onClick: () => void
  }>
  children?: React.ReactNode
}

/**
 * Post Card Component
 * Social media style post with avatar, content, and interactions
 * Based on shadcn/ui "Post Card" block
 */
export const UICardPost = React.forwardRef<HTMLDivElement, UICardPostProps>(
  ({
    author,
    content,
    timestamp,
    likes = 0,
    comments = 0,
    shares = 0,
    liked = false,
    imageUrl,
    imageAlt,
    onLike,
    onComment,
    onShare,
    onMenuClick,
    actions = [],
    className,
    children,
    ...props
  }, ref) => {
    const [isLiked, setIsLiked] = React.useState(liked)
    const [likeCount, setLikeCount] = React.useState(likes)

    const handleLike = () => {
      const newLikedState = !isLiked
      setIsLiked(newLikedState)
      setLikeCount(prev => newLikedState ? prev + 1 : prev - 1)
      onLike?.()
    }

    const formatTimestamp = (ts: string | Date) => {
      if (!ts) return ""
      const date = typeof ts === 'string' ? new Date(ts) : ts
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name: string) => {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }

    return (
      <UICard
        ref={ref}
        className={cn("w-full max-w-md", className)}
        {...props}
      >
        {/* Header with author info */}
        <UICardHeader className="pb-3">
          <UIBox className="flex items-center justify-between">
            <UIBox className="flex items-center gap-3">
              <UIAvatar className="h-10 w-10">
                <UIAvatarImage src={author.avatarUrl} alt={author.name} />
                <UIAvatarFallback>
                  {getInitials(author.name)}
                </UIAvatarFallback>
              </UIAvatar>

              <UIBox className="flex flex-col">
<UITypography variant="p" size="sm" weight="semibold" className="leading-none">
                  {author.name}
                </UITypography>
                {author.username && (
<UITypography variant="p" size="xs" color="muted">
                    @{author.username}
                  </UITypography>
                )}
                {timestamp && (
<UITypography variant="p" size="xs" color="muted">
                    {formatTimestamp(timestamp)}
                  </UITypography>
                )}
              </UIBox>
            </UIBox>

            {onMenuClick && (
              <UIButton
                variant="ghost"
                size="icon"
                onClick={onMenuClick}
                className="h-8 w-8"
              >
                <MoreHorizontal className="h-4 w-4" />
              </UIButton>
            )}
          </UIBox>
        </UICardHeader>

        {/* Content */}
        <UICardContent className="py-3">
<UITypography variant="p" size="sm" className="leading-relaxed">
            {content}
          </UITypography>

          {/* Image */}
          {imageUrl && (
            <UIBox className="mt-3 overflow-hidden rounded-lg">
              <img
                src={imageUrl}
                alt={imageAlt || "Post image"}
                className="w-full h-auto object-cover"
              />
            </UIBox>
          )}

          {/* Custom content */}
          {children}
        </UICardContent>

        {/* Footer with interactions */}
        <UICardFooter className="pt-3 border-t border-border/40">
          <UIBox className="flex items-center justify-between w-full">
            {/* Main actions */}
            <UIBox className="flex items-center gap-4">
              <UIButton
                variant="ghost"
                size="sm"
                onClick={handleLike}
                className={cn(
                  "h-8 px-2 gap-1",
                  isLiked && "text-red-600 hover:text-red-700"
                )}
              >
                <Heart
                  className={cn(
                    "h-4 w-4",
                    isLiked && "fill-current"
                  )}
                />
<UITypography as="span" variant="caption" size="xs">
                  {likeCount}
                </UITypography>
              </UIButton>

              <UIButton
                variant="ghost"
                size="sm"
                onClick={onComment}
                className="h-8 px-2 gap-1"
              >
                <MessageCircle className="h-4 w-4" />
<UITypography as="span" variant="caption" size="xs">
                  {comments}
                </UITypography>
              </UIButton>

              <UIButton
                variant="ghost"
                size="sm"
                onClick={onShare}
                className="h-8 px-2 gap-1"
              >
                <Share className="h-4 w-4" />
<UITypography as="span" variant="caption" size="xs">
                  {shares}
                </UITypography>
              </UIButton>
            </UIBox>

            {/* Custom actions */}
            {actions.length > 0 && (
              <UIBox className="flex items-center gap-1">
                {actions.map((action, index) => {
                  const Icon = action.icon
                  return (
                    <UIButton
                      key={index}
                      variant="ghost"
                      size="sm"
                      onClick={action.onClick}
                      className="h-8 w-8 p-0"
                      title={action.label}
                    >
                      <Icon className="h-4 w-4" />
                    </UIButton>
                  )
                })}
              </UIBox>
            )}
          </UIBox>
        </UICardFooter>
      </UICard>
    )
  }
)

UICardPost.displayName = "UICardPost"